/* J114 (TOM OK 22:23): OBF motion. Feedback only: 100-300 ms, ease-out, transform and opacity.
   Styles live in obf-motion.css. With "reduce motion" on, the CSS turns every transition off;
   the add-to-basket flow still works (instant states). Nothing here touches the hero. */
import { CartLinesUpdateEvent } from '@shopify/events';

const fineHover = matchMedia('(hover: hover) and (pointer: fine)');

/* 1. Card hover: on desktop, the product's 2nd photo (in-use) cross-fades in. The extra image is only
   created on hover, or once the page has loaded and the card is near the screen. Touch screens never get it. */
function altImage(box) {
  if (box.dataset.obfAltDone) return;
  box.dataset.obfAltDone = '1';
  const img = new Image();
  img.className = 'obf-alt';
  img.alt = '';
  img.decoding = 'async';
  img.sizes = box.dataset.obfAltSizes || '300px';
  img.srcset = box.dataset.obfAltSrcset;
  img.src = box.dataset.obfAlt;
  img.addEventListener('load', () => img.classList.add('is-ready'), { once: true });
  box.appendChild(img);
}
function initCards() {
  // J192: home page rows that show the lifestyle picture first: add it straight away (no studio flash).
  // J197 (Tom 00:54, audit decision 11): phones get the lifestyle picture too, so the rows match the PC.
  document.querySelectorAll('.obf-pt--rev [data-obf-alt]').forEach(altImage);
  if (!fineHover.matches) return;
  const boxes = document.querySelectorAll('[data-obf-alt]');
  boxes.forEach((b) => b.closest('a')?.addEventListener('pointerenter', () => altImage(b), { once: true }));
  const near = () => {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((es) => es.forEach((e) => {
      if (e.isIntersecting) { altImage(e.target); io.unobserve(e.target); }
    }), { rootMargin: '200px' });
    boxes.forEach((b) => io.observe(b));
  };
  if (document.readyState === 'complete') near(); else addEventListener('load', near, { once: true });
}

/* 5. Sections below the first screen: heading and row fade up once. Only sections that start below the
   fold are touched, and only after JS runs, so all content is visible without JS. Never the hero. */
function initReveal() {
  if (!('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const fold = innerHeight;
  const els = [...document.querySelectorAll('main .shopify-section')].filter((s) =>
    !s.querySelector('.obf-hero') && s.getBoundingClientRect().top > fold);
  if (!els.length) return;
  const io = new IntersectionObserver((es) => es.forEach((e) => {
    if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
  }), { rootMargin: '0px 0px -10% 0px' });
  els.forEach((s) => { s.classList.add('obf-rv'); io.observe(s); });
}

/* 3. Add to basket on our product pages: stays on the page, the button shows a tick and "Added",
   Horizon pops the basket count and refreshes the drawer (CartLinesUpdateEvent), then the drawer slides in.
   On failure the store's own error message shows and nothing pretends to have worked.
   Without JS (or if the drawer is missing) the plain /cart/add form still posts as before. */
function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
}
function showError(form, msg) {
  let box = form.querySelector('[data-obf-add-error]');
  if (!box) {
    box = document.createElement('p');
    box.className = 'obf-add-error';
    box.setAttribute('data-obf-add-error', '');
    box.setAttribute('role', 'alert');
    form.querySelector('[data-add]')?.insertAdjacentElement('afterend', box);
  }
  box.textContent = msg;
  box.hidden = false;
}
const TICK = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';
function added(btn) {
  let tag = btn.querySelector('.obf-added');
  if (!tag) {
    tag = document.createElement('span');
    tag.className = 'obf-added';
    tag.innerHTML = `${TICK}<span>Added</span>`;
    btn.appendChild(tag);
  }
  // Re-trigger the CSS fade each time.
  btn.classList.remove('is-added');
  void btn.offsetWidth;
  btn.classList.add('is-added');
  const live = document.getElementById('obf-add-live');
  if (live) { live.textContent = ''; setTimeout(() => { live.textContent = 'Added to your basket'; }, 50); }
  clearTimeout(btn._obfT);
  btn._obfT = setTimeout(() => btn.classList.remove('is-added'), 2000);
}
function initAdd() {
  if (!document.getElementById('obf-add-live')) {
    const live = document.createElement('p');
    live.id = 'obf-add-live';
    live.className = 'visually-hidden';
    live.setAttribute('role', 'status');
    document.body.appendChild(live);
  }
  const drawer = document.querySelector('cart-drawer-component');
  // Horizon declares Theme with const in a classic script: a global binding, not a window property.
  if (!drawer || !window.fetch || typeof Theme === 'undefined' || !Theme.routes?.cart_add_url) return;
  document.querySelectorAll('form[data-form]').forEach((form) => {
    const btn = form.querySelector('[data-add]');
    if (!btn) return;
    form.addEventListener('submit', async (ev) => {
      if (ev.defaultPrevented || btn.disabled || form.dataset.obfBusy) return;
      ev.preventDefault();
      form.dataset.obfBusy = '1';
      form.querySelector('[data-obf-add-error]')?.setAttribute('hidden', '');
      const fd = new FormData(form);
      const ids = cartSectionIds();
      if (ids.length) fd.append('sections', ids.join(','));
      const deferred = CartLinesUpdateEvent.createPromise();
      form.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add', context: 'product',
        lines: [{ merchandiseId: String(fd.get('id')), quantity: Number(fd.get('quantity')) || 1 }],
        promise: deferred.promise,
      }));
      let res, data;
      try {
        res = await fetch(Theme.routes.cart_add_url, {
          method: 'POST', body: fd, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        });
        data = await res.json();
      } catch (err) {
        // The add itself never got an answer: fall back to the normal form post (the store's own result page).
        deferred.reject(err);
        delete form.dataset.obfBusy;
        form.submit();
        return;
      }
      try {
        const cart = await (await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })).json();
        const ajax = CartLinesUpdateEvent.createCartFromAjaxResponse(cart);
        if (!res.ok || data.status) {
          deferred.resolve({ cart: ajax, detail: { didError: true, items: cart.items, source: 'obf-motion' } });
          showError(form, data.description || data.message || "Sorry, we couldn't add this to your basket. Please try again.");
          return;
        }
        deferred.resolve({ cart: ajax, detail: { didError: false, items: cart.items, sections: data.sections, source: 'obf-motion' } });
      } catch (err) {
        // Added, but the basket could not be re-read: show the basket page rather than guess.
        deferred.reject(err);
        if (res.ok && !data.status) location.href = Theme.routes.cart_url;
        else showError(form, (data && (data.description || data.message)) || "Sorry, we couldn't add this to your basket. Please try again.");
        return;
      } finally {
        delete form.dataset.obfBusy;
      }
      added(btn);
      const td = drawer.closest('theme-drawer');
      if (td && !td.isOpen) td.open();
    });
  });
}

initCards();
initReveal();
initAdd();
